import { useState, useEffect } from 'react';
import { Container } from '@mui/material';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import CustomTab from '../../components/client/CustomTab';
import Header from '../../components/client/Header';
import ServiceSection from '../../components/client/ServiceSection';
import TimetableSection from '../../components/client/TimetableSection';
import Loader from '../../components/common/Loader';
import { IAppointment, IBusiness, IProvider, IService, IUnavailability } from '../../models/api/entities';
import { fetcher } from '../../modules/utils/Utils';
import { RootState } from '../../store';
import axios from 'axios';

dayjs.extend(utc);
dayjs.extend(isBetween);
dayjs.extend(timezone);

const withData = (Component: any) => (props: any) => {
    const businessSlug = useSelector((state: RootState) => state.backoffice.businessSlug);

    const { data: business, error: businessError } = useSWR<IBusiness>(`/api/business/${businessSlug}`, fetcher);
    const { data: providers, error: providersError } = useSWR<IProvider[]>(`/api/provider/`, fetcher);
    const { data: appointments, error: appointmentsError } = useSWR<IAppointment[]>(
        `/api/appointment-table/?date=${dayjs().startOf('day').format('YYYY-MM-DD')}`,
        fetcher
    );
    const { data: unavailabilities, error: unavailabilitiesError } = useSWR<IUnavailability[]>(`/api/unavailability/`, fetcher);
    const { data: services, error: servicesError } = useSWR<IService[]>(`/api/service/`, fetcher);

    if (businessError || providersError || appointmentsError || servicesError || unavailabilitiesError)
        return <div>Error fetching data!</div>; // TODO: Show error overlay component
    if (!business || !providers || !appointments || !services || !unavailabilities) return <Loader />;

    return (
        <Component
            business={business}
            providers={providers}
            appointments={appointments}
            services={services}
            unavailabilities={unavailabilities}
            {...props}
        />
    );
};

interface Props {
    business: IBusiness;
    providers: IProvider[];
    appointments: IAppointment[];
    services: IService[];
    unavailabilities: IUnavailability[];
}

const BusinessIndex = ({ business, providers, appointments, services, unavailabilities }: Props) => {
    const [curAppointments, setCurAppointments] = useState(appointments);
    const [curDate, setCurDate] = useState<dayjs.Dayjs>();
    useEffect(() => {
        if (curDate) {
            (async () => {
                const tempAppointments = await axios.get<IAppointment[]>(`/api/appointment-table/?date=${curDate.format('YYYY-MM-DD')}`);
                setCurAppointments(tempAppointments.data);
            })();
        }
    }, [curDate]);
    return (
        <>
            <Header business={business} />
            <CustomTab
                booking={
                    <Container maxWidth="sm" sx={{ padding: 0 }}>
                        <TimetableSection
                            business={business}
                            appointments={curAppointments}
                            providers={providers}
                            unavailabilities={unavailabilities}
                            setCurDate={setCurDate}
                        />
                    </Container>
                }
                service={
                    <Container maxWidth="sm">
                        <ServiceSection services={services} />
                    </Container>
                }
            />
        </>
    );
};

export default withData(BusinessIndex);
